import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Send } from 'lucide-react';
import { useModalZIndex } from '../contexts/ModalStackContext';

interface InvoiceSMSModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (message: string, allowStripePay: boolean, depositAmount?: number, saveCardOnFile?: boolean, recipientPhone?: string) => void;
  customerPhone: string;
  customerName: string;
  businessName: string;
  bookingNumber: number;
  hasStripeConnected: boolean;
  amountOwing: number;
  canOfferSaveCardOnFile?: boolean;
  /** When true, the recipient phone can be edited for this send only (doesn't change the customer). */
  allowEditRecipient?: boolean;
  isSending?: boolean;
  initialMessage?: string;
}

export default function InvoiceSMSModal({
  isOpen,
  onClose,
  onSend,
  customerPhone,
  customerName,
  businessName,
  bookingNumber,
  hasStripeConnected,
  amountOwing,
  canOfferSaveCardOnFile = false,
  allowEditRecipient = false,
  isSending = false,
  initialMessage,
}: InvoiceSMSModalProps) {
  const stackZ = useModalZIndex(100060);
  const [isAnimating, setIsAnimating] = useState(false);
  const [message, setMessage] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [allowStripePay, setAllowStripePay] = useState(true);
  const [saveCardOnFile, setSaveCardOnFile] = useState(false);
  const [paymentType, setPaymentType] = useState<'full' | 'deposit'>('full');
  const [depositAmount, setDepositAmount] = useState('');

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setMessage(initialMessage || `Hi ${customerName}, here is your invoice #${bookingNumber} from ${businessName}.`);
      setRecipientPhone(customerPhone || '');
      setAllowStripePay(true);
      setSaveCardOnFile(false);
      setPaymentType('full');
      setDepositAmount('');
    }
  }, [isOpen, customerName, businessName, bookingNumber, initialMessage, customerPhone]);

  useEffect(() => {
    if (!allowStripePay) setSaveCardOnFile(false);
  }, [allowStripePay]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const phoneDigits = (allowEditRecipient ? recipientPhone : customerPhone).replace(/[^\d]/g, '');
  const recipientValid = phoneDigits.length >= 7;
  const effectiveRecipient = (allowEditRecipient ? recipientPhone : customerPhone).trim();

  const handleSend = () => {
    if (!message.trim() || !recipientValid) return;
    const deposit = paymentType === 'deposit' && depositAmount ? parseFloat(depositAmount) : undefined;
    const save = allowStripePay && canOfferSaveCardOnFile && saveCardOnFile;
    onSend(message, allowStripePay, deposit, save, effectiveRecipient);
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: stackZ,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`bg-white transform transition-all duration-200 fixed inset-0 w-full h-full sm:inset-y-0 sm:left-auto sm:right-0 sm:w-[600px] sm:h-auto shadow-2xl ${
          isOpen
            ? 'translate-y-0 sm:translate-y-0 sm:translate-x-0 opacity-100'
            : 'translate-y-full sm:translate-y-0 sm:translate-x-full opacity-0 sm:opacity-100'
        }`}
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        }}
        onTransitionEnd={handleTransitionEnd}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          <button
            onClick={onClose}
            className="absolute right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{
              top: 'calc(1rem + env(safe-area-inset-top, 0px))',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <X className="h-5 w-5" />
          </button>

          <div
            className="bg-gray-50 border-b border-gray-200 px-6 py-6"
            style={{
              paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))',
            }}
          >
            <h2
              className="text-2xl font-bold text-gray-900 text-center"
              style={{ letterSpacing: '-0.02em' }}
            >
              Send Invoice SMS
            </h2>
          </div>

          <div
            className="flex-1 overflow-y-auto bg-white"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="p-4 sm:p-6 space-y-5">
              {/* Stripe toggle */}
              {amountOwing > 0 && (
                <div className={`border rounded-xl p-4 space-y-3 ${hasStripeConnected ? 'bg-gray-50 border-gray-200' : 'bg-gray-50/60 border-gray-200'}`}>
                  <div
                    className={`flex items-center justify-between ${hasStripeConnected ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                    onClick={() => { if (hasStripeConnected) setAllowStripePay(!allowStripePay); }}
                  >
                    <div className="flex items-center space-x-3">
                      <img src="/Stripe (2).png" alt="Stripe" className={`w-6 h-6 ${!hasStripeConnected ? 'opacity-40 grayscale' : ''}`} />
                      <div>
                        <span className={`text-sm font-medium ${hasStripeConnected ? 'text-gray-700' : 'text-gray-400'}`}>
                          Include Stripe Payment Link
                        </span>
                        {!hasStripeConnected && (
                          <p className="text-xs text-gray-400 mt-0.5">Configure Stripe to collect real payments</p>
                        )}
                      </div>
                    </div>
                    <div
                      className={`w-10 h-6 rounded-full transition-colors relative ${
                        !hasStripeConnected ? 'bg-gray-200' : allowStripePay ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          !hasStripeConnected ? 'translate-x-0.5' : allowStripePay ? 'translate-x-[18px]' : 'translate-x-0.5'
                        }`}
                      />
                    </div>
                  </div>

                  {hasStripeConnected && allowStripePay && (
                    <div className="pt-2 border-t border-gray-200 space-y-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setPaymentType('full')}
                          className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                            paymentType === 'full'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Full Balance (${amountOwing.toFixed(2)})
                        </button>
                        <button
                          onClick={() => setPaymentType('deposit')}
                          className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                            paymentType === 'deposit'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Deposit
                        </button>
                      </div>
                      {paymentType === 'deposit' && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">$</span>
                          <input
                            type="number"
                            value={depositAmount}
                            onChange={(e) => setDepositAmount(e.target.value)}
                            placeholder="Enter deposit amount"
                            className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            style={{ fontSize: '16px' }}
                            min="0"
                            max={amountOwing}
                            step="0.01"
                          />
                        </div>
                      )}
                      {canOfferSaveCardOnFile && (
                        <div
                          className="flex items-center justify-between pt-2 border-t border-gray-200"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div>
                            <span className="text-sm font-medium text-gray-700">Save card on file</span>
                            <p className="text-xs text-gray-500 mt-0.5">
                              When they pay this link, store their card for later. Replaces any existing saved card.
                            </p>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={saveCardOnFile}
                            onClick={() => setSaveCardOnFile((v) => !v)}
                            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                              saveCardOnFile ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition mt-0.5 ${
                                saveCardOnFile ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">To</label>
                {allowEditRecipient ? (
                  <>
                    <input
                      type="tel"
                      value={recipientPhone}
                      onChange={(e) => setRecipientPhone(e.target.value)}
                      className="w-full px-3 py-2.5 sm:px-4 sm:py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                      style={{ fontSize: '16px', minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                      placeholder="+1 555 123 4567"
                    />
                    {!recipientValid && recipientPhone.trim().length > 0 && (
                      <p className="text-xs text-red-500">Enter a valid phone number.</p>
                    )}
                    <p className="text-xs text-gray-500">
                      Sends to this number only — the customer's saved phone won't change.
                    </p>
                  </>
                ) : (
                  <div className="px-3 py-2.5 sm:px-4 sm:py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700">
                    {customerPhone}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none touch-manipulation"
                  style={{ fontSize: '16px', WebkitTapHighlightColor: 'transparent' }}
                  placeholder="Enter your message"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    The payment link will be appended automatically if enabled. You can edit this message before sending.
                  </p>
                  <p className="text-xs text-gray-400 whitespace-nowrap ml-2">
                    {message.length} chars
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            className="border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4"
            style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-3 py-2.5 sm:px-4 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={
                  isSending ||
                  !message.trim() ||
                  !recipientValid ||
                  (allowStripePay && paymentType === 'deposit' && (!depositAmount || parseFloat(depositAmount) <= 0))
                }
                className={`flex-1 px-3 py-2.5 sm:px-4 text-sm font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm ${
                  !isSending && message.trim() && recipientValid
                    ? 'bg-green-600 hover:bg-green-700 active:bg-green-800'
                    : 'bg-gray-300 cursor-not-allowed'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {isSending ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Sending...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Send className="h-4 w-4 mr-2" />
                    Send SMS
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
